// 🔹 React Core
import React, { useEffect, useMemo, useState, useRef } from "react";

// 🔹 Routing
import { useNavigate } from "react-router-dom";

// 🔹 State Management
import { RootState, useAppDispatch, useAppSelector } from "../../../../store";

// 🔹 UI Component Libraries
import {
  Dropdown,
  Menu,
  Spin,
  Table,
  Modal,
  Tag,
  Tabs,
  Grid,
  Input,
} from "antd";
import type { TableColumnsType } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

// 🔹 Internal Components
import FullScreenLoader from "../../../Components/FullScreenLoader";
import NoDataMessage from "../../../Components/NoData";
import NoPermissionPage from "../../../Components/NoPermissionPage";
import NotificationButton from "../../../Components/NotificationButton";
import DeliveryMarkup from "./DeliveryMarkup";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";

// 🔹 Internal Utilities
import {
  CapitalizeFirstWord,
  formatToLocalDate,
  getAntdSortOrder,
  useDebounce,
} from "../../../../helpers/utils";
import { getDecryptedName } from "../../../../helpers/bycrypt";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import {
  GetDeliveryZoneListAPI,
  DeleteDeliveryZoneAPI,
  GetDeliveryMarkupAPI,
  ReorderDeliveryZoneAPI,
} from "../../../Redux/deliveryPricing";
import { GetCountryListAPI } from "../../../Redux/country";

import { svg } from "../../../../Assets/svg";
import { SortableDeliveryPricingCard } from "./SortableDeliveryPricingCard";

const { useBreakpoint } = Grid;

type DeliveryPricingCardProps = {
  item?: any;
  country?: any;
  markupPercentage?: number;
  isReorderMode?: boolean;
  onDeleteSuccess?: (deletedId: string) => void;
};

export const DeliveryPricingCard: React.FC<DeliveryPricingCardProps> = ({
  item,
  country,
  markupPercentage = 0,
  isReorderMode = false,
  onDeleteSuccess,
}) => {
  // Use
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const prefixSpanRef = useRef<HTMLSpanElement>(null);
  const [isPrefixTruncated, setIsPrefixTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (prefixSpanRef.current) {
        if (!isExpanded) {
          setIsPrefixTruncated(
            prefixSpanRef.current.scrollWidth >
              prefixSpanRef.current.clientWidth,
          );
        }
      }
    };

    // Initial check
    checkTruncation();

    // Check on window resize
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [item?.area_codes, isExpanded]);

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;
    setDeleting(true);
    dispatch(
      DeleteDeliveryZoneAPI(idToDelete, () => {
        setDeleting(false);
        const deleted = idToDelete;
        setIdToDelete(null);
        if (onDeleteSuccess) {
          onDeleteSuccess(deleted);
        } else {
          dispatch(GetDeliveryZoneListAPI(1, 30, "") as any);
        }
      }) as any,
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  // Permission
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="modify"
            onClick={() =>
              navigate(
                `/master-data/delivery-pricing/create-zone/${
                  data?.zone_id || item?.zone_id
                }`,
              )
            }
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item
            key="2"
            onClick={() => {
              setIdToDelete(data?.zone_id || item?.zone_id);
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const isNameBy =
    item?.updated_by?.first_name ||
    item?.updated_by?.last_name ||
    item?.updated_at;
  const name = item?.updated_by ? getDecryptedName(item.updated_by) : "-";
  const on = formatToLocalDate(item?.updated_at, true);

  return (
    <div className="card border-0 shadow-sm rounded-4 mb-3 p-3 p-md-4">
      <div className="card-body">
        {/* Header */}
        <div className="d-flex flex-column gap-1">
          {/* Top row: Title and Actions (centered together on mobile and desktop) */}
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="fw-bold mb-0 text-truncate">{item?.zone_name}</h5>
            </div>

            <div className="d-flex align-items-center gap-3 flex-shrink-0">
              <h6 className="fs-14 fw-normal text-secondary mb-0 d-none d-sm-block text-nowrap">
                {isNameBy ? `Updated By: ${name || "-"} on ${on || "-"}` : `-`}
              </h6>

              {!isReorderMode &&
                (USER_PERMISSIONS?.MODIFY || USER_PERMISSIONS?.REMOVE) && (
                  <Dropdown
                    overlay={() => MoreOptionsMenu(item)}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="cursor-pointer d-flex align-items-center"
                    overlayClassName="moreOptionDropdown"
                  >
                    <span className="svg-32 svg-outline-primary" role="button">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                )}
            </div>
          </div>

          {/* Mobile only: Updated By on a clean new line */}
          <h6 className="fs-14 fw-normal text-secondary mb-0 d-sm-none text-truncate">
            {isNameBy ? `Updated By: ${name || "-"} on ${on || "-"}` : `-`}
          </h6>
        </div>

        <div className="row gy-2 mt-1">
          <div className="col-12">
            <div className="d-flex flex-wrap align-items-center gap-2 fs-14">
              <strong className="text-dark text-nowrap">
                Dispatch to Delivery Allowance:
              </strong>
              <div className="text-secondary text-nowrap">
                <span className="text-dark fw-medium">
                  {item?.dispatch_to_delivery_allowance !== undefined &&
                  item?.dispatch_to_delivery_allowance !== null &&
                  item?.dispatch_to_delivery_allowance !== ""
                    ? `${item.dispatch_to_delivery_allowance} ${
                        Number(item.dispatch_to_delivery_allowance) === 1
                          ? "Day"
                          : "Days"
                      }`
                    : "-"}
                </span>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="d-flex flex-wrap align-items-center gap-2 fs-14">
              <strong className="text-dark text-nowrap">Normal Price:</strong>
              <div className="text-secondary text-nowrap">
                Pallet Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.normal_price?.pallet_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Half Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.normal_price?.tailer?.half_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Full Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.normal_price?.tailer?.full_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Mezzanine Half Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(
                    item?.normal_price?.mezzanine_tailer
                      ?.mezzanine_half_price || 0,
                  ).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Mezzanine Full Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(
                    item?.normal_price?.mezzanine_tailer
                      ?.mezzanine_full_price || 0,
                  ).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Fork Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.normal_price?.fork_lift_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Boom Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.normal_price?.boom_lift_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Scissor Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.normal_price?.scissor_lift_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className={`d-flex flex-wrap align-items-center gap-2 fs-14`}>
              <strong className="text-dark text-nowrap">Markup Price:</strong>
              <div className="text-secondary text-nowrap">
                Pallet Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.markup_price?.pallet_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Half Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.markup_price?.tailer?.half_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Full Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.markup_price?.tailer?.full_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Mezzanine Half Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(
                    item?.markup_price?.mezzanine_tailer
                      ?.mezzanine_half_price || 0,
                  ).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Mezzanine Full Load (Trailer) Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(
                    item?.markup_price?.mezzanine_tailer
                      ?.mezzanine_full_price || 0,
                  ).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Fork Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.markup_price?.fork_lift_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Boom Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £{Number(item?.markup_price?.boom_lift_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="text-secondary text-nowrap">
                Scissor Lift Transport Price:{" "}
                <span className="text-dark fw-medium">
                  £
                  {Number(item?.markup_price?.scissor_lift_price || 0).toFixed(
                    2,
                  )}
                </span>
              </div>
            </div>
          </div>
          {item?.box_prices && item.box_prices.length > 0 && (
            <div className="col-12">
              <div className="d-flex flex-wrap align-items-center gap-2 fs-14">
                <strong className="text-dark text-nowrap">
                  Parcel Delivery Prices:
                </strong>
                {item.box_prices.map((box: any, index: number) => (
                  <div
                    className="text-secondary text-nowrap"
                    key={box.box_id || box._id || index}
                  >
                    {box.name}:{" "}
                    <span className="text-dark fw-medium">
                      £{Number(box.price || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {item?.markup_box_prices && item.markup_box_prices.length > 0 && (
            <div className="col-12">
              <div className="d-flex flex-wrap align-items-center gap-2 fs-14">
                <strong className="text-dark text-nowrap">
                  Parcel Delivery Markup Prices:
                </strong>
                {item.markup_box_prices.map((box: any, index: number) => (
                  <div
                    className="text-secondary text-nowrap"
                    key={box.box_id || box._id || index}
                  >
                    {box.name}:{" "}
                    <span className="text-dark fw-medium">
                      £{Number(box.price || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center gap-3 mt-2 fs-14">
          <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
            <strong className="text-dark me-2 mb-0 text-nowrap align-self-start">
              Prefix:
            </strong>
            <span
              ref={prefixSpanRef}
              className={`text-secondary mb-0 ${
                isExpanded ? "text-wrap" : "text-truncate"
              }`}
            >
              {item?.area_codes && Array.isArray(item.area_codes)
                ? item.area_codes.map((area: any, index: number) => (
                    <React.Fragment key={index}>
                      {area.area_name
                        ? `${area.prefix} (${area.area_name})`
                        : area.prefix}
                      {/* {area.cross_border ? (
                      <span className="text-secondary"> (Cross-Border)</span>
                    ) : (
                      ""
                    )} */}
                      {index < item.area_codes.length - 1 ? ", " : ""}
                    </React.Fragment>
                  ))
                : "-"}
            </span>
          </div>
          {(isPrefixTruncated || isExpanded) && (
            <span
              className="text-dark fw-medium fs-14 flex-shrink-0 cursor-pointer text-decoration-underline align-self-start"
              role="button"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Show Less" : "View All"}
            </span>
          )}
        </div>
      </div>

      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Zone, Are You Sure?"
        subTitle="Removing this zone will delete all related information. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
      />
    </div>
  );
};

const DeliveryPricing = () => {
  // Use
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );

  const { getDeliveryPricingLoading, authLoading } = useAppSelector(
    (state: RootState) => state?.loading,
  );

  // Selector
  const {
    DeliveryPricingList = [],
    DeliveryPricingCount,
    DeliveryMarkupPercentage,
  } = useAppSelector((state: RootState) => state?.deliveryPricing);

  const { countryList } = useAppSelector((state: RootState) => state?.country);

  const [limit] = useState<number>(30);
  const [activeTab, setActiveTab] = useState<string>("England");
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState(0);
  const [editDeliveryMarkupModal, setEditDeliveryMarkupModal] = useState({
    open: false,
    data: null,
  });

  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [reordering, setReordering] = useState<boolean>(false);
  const [localDeliveryPricingList, setLocalDeliveryPricingList] = useState<
    any[]
  >([]);
  const screens = useBreakpoint();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [tabSearch, setTabSearch] = useState("");

  const totalZoneCount = useMemo(() => {
    return localDeliveryPricingList.reduce(
      (acc, curr) => acc + (curr.zones?.length || 0),
      0,
    );
  }, [localDeliveryPricingList]);

  const countrySequence = useMemo(() => {
    const predefinedOrder = [
      "England",
      "Republic of Ireland",
      "Wales",
      "Scotland",
      "Northern Ireland",
      "The Netherlands",
      "Belgium",
    ];

    if (countryList && countryList.length > 0) {
      const fetchedCountries = countryList.map((c: any) => c.country_name);

      // Get all countries in predefined order that actually exist in fetched list
      const orderedCountries = predefinedOrder.filter((country) =>
        fetchedCountries.includes(country),
      );

      // Get any extra countries that aren't in predefined order
      const extraCountries = fetchedCountries.filter(
        (country: string) => !predefinedOrder.includes(country),
      );

      return [...orderedCountries, ...extraCountries];
    }

    return predefinedOrder;
  }, [countryList]);

  // Tab list specifically filtered by tabSearch (does not affect right-side card sequence)
  const filteredCountryTabs = useMemo(() => {
    if (tabSearch.trim()) {
      const q = tabSearch.trim().toLowerCase();
      return countrySequence.filter((country) =>
        country.toLowerCase().includes(q),
      );
    }
    return countrySequence;
  }, [countrySequence, tabSearch]);

  useEffect(() => {
    setLocalDeliveryPricingList(DeliveryPricingList || []);
  }, [DeliveryPricingList]);

  const sortedDeliveryPricingList = useMemo(() => {
    if (!localDeliveryPricingList || localDeliveryPricingList.length === 0)
      return [];
    return [...localDeliveryPricingList].sort((a: any, b: any) => {
      const indexA = countrySequence.indexOf(a?.country_name);
      const indexB = countrySequence.indexOf(b?.country_name);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return a?.country_name?.localeCompare(b?.country_name);
    });
  }, [localDeliveryPricingList, countrySequence]);

  // Debounce
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    dispatch(GetCountryListAPI() as any);
    dispatch(GetDeliveryMarkupAPI() as any);
    dispatch(
      GetDeliveryZoneListAPI(currentPage, limit, debouncedSearch) as any,
    );
  }, [dispatch, currentPage, limit, debouncedSearch]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const countryDataIndex = localDeliveryPricingList.findIndex((c) =>
      c.zones.some((z: any) => z.zone_id === active.id),
    );
    if (countryDataIndex === -1) return;

    const countryData = localDeliveryPricingList[countryDataIndex];
    const zones = [...countryData.zones];

    const activeIndex = zones.findIndex((i: any) => i.zone_id === active.id);
    const overIndex = zones.findIndex((i: any) => i.zone_id === over.id);

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const targetOrder = zones[overIndex]?.order ?? 0;

    const reorderedZones = arrayMove(zones, activeIndex, overIndex);
    const newLocalList = [...localDeliveryPricingList];
    newLocalList[countryDataIndex] = { ...countryData, zones: reorderedZones };
    setLocalDeliveryPricingList(newLocalList);

    setReordering(true);
    dispatch(
      ReorderDeliveryZoneAPI(
        {
          id: active.id as string,
          order: targetOrder,
          country_id: countryData.country_id,
        },
        () => {
          setReordering(false);
          dispatch({
            type: "GET_DELIVERY_PRICING_DATA",
            payload: {
              data: newLocalList,
              count: DeliveryPricingCount,
              page: 1,
            },
          });
        },
      ) as any,
    );
  };

  const headerMoreMenu = (
    <Menu>
      {USER_PERMISSIONS?.MODIFY && (
        <Menu.Item
          key="1"
          onClick={() => {
            setIsReorderMode(true);
            setSearch("");
          }}
        >
          Reorder
        </Menu.Item>
      )}
    </Menu>
  );

  return USER_PERMISSIONS?.READ ? (
    <div className="page-content h-100 px-40 py-40 d-flex flex-column">
      <div className="flex-shrink-0">
        {isReorderMode ? (
          <div className="mb-sm-4 mb-3 gap-2 d-flex flex-wrap justify-content-sm-between justify-content-center align-items-center">
            <div className="d-flex align-items-center gap-sm-3 gap-2 mob-w-100">
              <span
                className="cursor-pointer svg-24"
                onClick={() => {
                  setIsReorderMode(false);
                  setCurrentPage(1);
                  dispatch(
                    GetDeliveryZoneListAPI(1, limit, debouncedSearch) as any,
                  );
                }}
              >
                {svg?.backArrow || "←"}
              </span>
              <h2 className="text-black fs-36 fw-bolder page-title mb-0">
                Reorder Zones
              </h2>
            </div>
            <div className="row mob-w-100">
              <div className="col-sm-auto col-6">
                <button
                  className="btn btn-outline-secondary px-4 fw-bold w-100"
                  onClick={() => {
                    setIsReorderMode(false);
                    setCurrentPage(1);
                    dispatch(
                      GetDeliveryZoneListAPI(1, limit, debouncedSearch) as any,
                    );
                  }}
                  disabled={reordering}
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-sm-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-sm-center align-items-start mb-md-4 mb-3">
            <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-md-1 order-1 w-mob-38">
              <span>Delivery Pricing</span>{" "}
              {getDeliveryPricingLoading && currentPage === 1
                ? ""
                : totalZoneCount > 0
                ? `(${totalZoneCount})`
                : ""}
            </h2>
            <div
              className={`pageSearch mw-100 position-relative d-inline-block order-sm-2 order-3 ms-auto`}
            >
              <input
                type="text"
                value={search}
                className="form-control form-control-lg"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
              />
              {search?.length ? (
                <span className="cursor-pointer" onClick={() => setSearch("")}>
                  {svg?.searchClear}
                </span>
              ) : (
                <span>{svg?.search}</span>
              )}
            </div>
            <div className="d-flex gap-md-3 gap-2 order-sm-3 order-2">
              {USER_PERMISSIONS?.ADD ? (
                <>
                  <div
                    className="filterButton position-relative cursor-pointer"
                    onClick={() =>
                      setEditDeliveryMarkupModal({ open: true, data: null })
                    }
                  >
                    {svg?.deliverySettings}
                  </div>

                  {DeliveryPricingList &&
                    DeliveryPricingList.length > 0 &&
                    USER_PERMISSIONS?.MODIFY && (
                      <Dropdown
                        overlay={headerMoreMenu}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <span className="cursor-pointer svg-46">
                          {svg?.tableMoreOption}
                        </span>
                      </Dropdown>
                    )}

                  <button
                    className="btn btn-lg btn-primary mob-btn text-nowrap"
                    onClick={() => {
                      navigate("/master-data/delivery-pricing/create-zone");
                      dispatch({ type: "CLEAR_PREFIX_DATA" });
                    }}
                  >
                    + CREATE
                  </button>
                </>
              ) : (
                ""
              )}
              <NotificationButton />
            </div>
          </div>
        )}
      </div>

      <div className="row gy-3 flex-fill overflow-hidden">
        {/* Sidebar */}
        <div className="col-lg-2">
          <div className="customTabs create-quotation-tabs bg-white border-0 shadow rounded-3">
            {!screens.lg && (
              <button
                type="button"
                className="quotation-mobile-nav-trigger"
                onClick={() => setIsMobileNavOpen((prev) => !prev)}
              >
                <span className="quotation-mobile-nav-trigger-label">
                  <span className="quotation-mobile-nav-trigger-title">
                    {activeTab} (
                    {localDeliveryPricingList?.find(
                      (c: any) => c.country_name === activeTab,
                    )?.zones?.length || 0}
                    )
                  </span>
                </span>
                <span
                  className={`quotation-side-nav-group-arrow ${
                    isMobileNavOpen ? "is-expanded" : ""
                  }`}
                >
                  {svg?.selectDownArrow}
                </span>
              </button>
            )}

            <div
              className={`quotation-side-nav ${
                !screens.lg && !isMobileNavOpen ? "d-none" : ""
              }`}
            >
              <div className="px-2 pb-2 pt-1 border-bottom">
                <div className="pageSearch w-100 position-relative">
                  <input
                    type="text"
                    value={tabSearch}
                    className="form-control form-control-lg"
                    onChange={(e) => setTabSearch(e.target.value)}
                    placeholder="Search countries"
                  />
                  {tabSearch?.length ? (
                    <span
                      className="cursor-pointer"
                      onClick={() => setTabSearch("")}
                    >
                      {svg?.searchClear}
                    </span>
                  ) : (
                    <span>{svg?.search}</span>
                  )}
                </div>
              </div>

              {filteredCountryTabs.length === 0 ? (
                <div className="text-muted text-center py-3 fs-13">
                  No country found
                </div>
              ) : (
                filteredCountryTabs.map((country: string) => {
                  const countryData = localDeliveryPricingList?.find(
                    (c: any) => c.country_name === country,
                  );
                  const zoneCount = countryData?.zones?.length || 0;
                  const isActive = activeTab === country;

                  return (
                    <button
                      key={country}
                      type="button"
                      className={`quotation-side-nav-item ${
                        isActive ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setActiveTab(country);
                        if (!screens.lg) {
                          setIsMobileNavOpen(false);
                        }
                        const scrollContainer =
                          document.getElementById("scrollableDiv");
                        const element = document.getElementById(
                          `country-${country}`,
                        );
                        if (scrollContainer && element) {
                          const containerRect =
                            scrollContainer.getBoundingClientRect();
                          const elementRect = element.getBoundingClientRect();
                          const offset =
                            elementRect.top -
                            containerRect.top +
                            scrollContainer.scrollTop;
                          scrollContainer.scrollTo({
                            top: Math.max(0, offset),
                            behavior: "smooth",
                          });
                        } else if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="text-truncate me-2">{country}</span>
                        <span
                          className={`badge rounded-pill fw-medium ${
                            isActive
                              ? "bg-white text-dark"
                              : "bg-light text-secondary border"
                          }`}
                        >
                          {zoneCount}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="col-lg-10 d-flex flex-column h-100 overflow-hidden">
          <div className="page-table-card border-0 flex-fill overflow-y-auto">
            <div
              className="overflow-y-auto h-100 no-scrollbar"
              id="scrollableDiv"
              style={{ paddingBottom: !screens.lg ? "80px" : "20px" }}
            >
              {getDeliveryPricingLoading ? (
                <FullScreenLoader />
              ) : DeliveryPricingList.length === 0 ? (
                <NoDataMessage
                  icon={svg?.noDeliveryLocations}
                  text="You do not have any delivery pricing yet."
                />
              ) : (
                <>
                  {sortedDeliveryPricingList?.map(
                    (countryData: any, index: number) => (
                      <div
                        className="mb-3 mb-md-4"
                        key={index}
                        id={`country-${countryData?.country_name}`}
                      >
                        <h5 className="fs-5 fw-bold text-black mb-3">
                          {countryData?.country_name} (
                          {countryData?.zones?.length || 0})
                        </h5>
                        {countryData?.zones?.length > 0 ? (
                          isReorderMode ? (
                            <DndContext
                              id={`dnd-${countryData?.country_name}`}
                              sensors={sensors}
                              modifiers={[restrictToVerticalAxis]}
                              onDragEnd={onDragEnd}
                            >
                              <SortableContext
                                items={countryData.zones.map(
                                  (i: any) => i.zone_id,
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                {countryData.zones.map((zone: any) => (
                                  <SortableDeliveryPricingCard
                                    key={zone?.zone_id}
                                    item={zone}
                                    country={countryData?.country_name}
                                    markupPercentage={DeliveryMarkupPercentage}
                                    isReorderMode={isReorderMode}
                                    onDeleteSuccess={(deletedId: string) => {
                                      const updatedList =
                                        localDeliveryPricingList.map((c) => ({
                                          ...c,
                                          zones: c.zones.filter(
                                            (z: any) => z.zone_id !== deletedId,
                                          ),
                                        }));
                                      dispatch({
                                        type: "GET_DELIVERY_PRICING_DATA",
                                        payload: {
                                          data: updatedList,
                                          count: DeliveryPricingCount,
                                          page: 1,
                                        },
                                      });
                                    }}
                                  />
                                ))}
                              </SortableContext>
                            </DndContext>
                          ) : (
                            countryData?.zones?.map((zone: any) => (
                              <SortableDeliveryPricingCard
                                key={zone?.zone_id}
                                item={zone}
                                country={countryData?.country_name}
                                markupPercentage={DeliveryMarkupPercentage}
                                isReorderMode={false}
                                onDeleteSuccess={(deletedId: string) => {
                                  const updatedList =
                                    localDeliveryPricingList.map((c) => ({
                                      ...c,
                                      zones: c.zones.filter(
                                        (z: any) => z.zone_id !== deletedId,
                                      ),
                                    }));
                                  dispatch({
                                    type: "GET_DELIVERY_PRICING_DATA",
                                    payload: {
                                      data: updatedList,
                                      count: DeliveryPricingCount,
                                      page: 1,
                                    },
                                  });
                                }}
                              />
                            ))
                          )
                        ) : (
                          <div className="card border-0 shadow-sm rounded-4 mb-3 p-3 p-md-4">
                            <div className="text-center text-secondary">
                              No delivery zones available for{" "}
                              {countryData?.country_name}.
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeliveryMarkup
        onSuccess={() => {
          setEditDeliveryMarkupModal({ open: false, data: null });
          dispatch(
            GetDeliveryZoneListAPI(currentPage, limit, debouncedSearch) as any,
          );
          dispatch(GetDeliveryMarkupAPI() as any);
        }}
        options={editDeliveryMarkupModal}
        setState={() => setEditDeliveryMarkupModal({ open: false, data: null })}
      />
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default DeliveryPricing;
